import type {
  AnimationApplyContext,
  AnimationBindingTarget,
  AnimationClipData,
  AnimationLoopMode,
  AnimationPlayerEvent,
  AnimationPlayerEventPayload,
  AnimationPlayerHandle,
  AnimationPlayerState,
  AnimationPlayOptions,
  AnimationPropertySample,
  AnimationTargetRef,
  AnimationTrackData,
} from '@/shared/types/animation'
import type { Disposable } from '@/shared/types/core'
import type { AnimationBindingRegistry } from './AnimationBindingRegistry'
import { normalizeAnimationTime, sampleAnimationTrack } from './sampler'
import { validateAnimationClip } from './validation'

interface ResolvedTrack {
  track: AnimationTrackData
  target: AnimationBindingTarget
  sample: AnimationPropertySample
}

interface SampleGroup {
  target: AnimationBindingTarget
  samples: AnimationPropertySample[]
}

export class AnimationPlayerImpl implements AnimationPlayerHandle {
  readonly id: string
  readonly clip: AnimationClipData

  private readonly duration: number
  private readonly loopMode: AnimationLoopMode
  private readonly resolvedTracks: ResolvedTrack[] = []
  private readonly listeners = new Map<
    AnimationPlayerEvent,
    Set<(payload: AnimationPlayerEventPayload) => void>
  >()
  private readonly sampleGroups = new Map<string, SampleGroup>()

  private _state: AnimationPlayerState = 'paused'
  private _time = 0
  private _speed = 1

  constructor(
    id: string,
    clip: AnimationClipData,
    registry: AnimationBindingRegistry,
    options: AnimationPlayOptions = {}
  ) {
    this.id = id
    this.clip = clip
    this.duration = validateAnimationClip(clip)
    this.loopMode = options.loopMode ?? clip.loopMode ?? 'once'
    this._time = options.startTime ?? 0
    this._speed = options.speed ?? 1

    for (const track of clip.tracks) {
      const target = registry.resolve(track.target)
      if (!target) {
        throw new Error(
          `Animation target "${track.target.kind}" cannot be resolved for track "${
            track.name ?? track.propertyPath
          }"`
        )
      }
      if (target.supportedProperties && !target.supportedProperties.includes(track.propertyPath)) {
        throw new Error(
          `Animation target "${target.key}" does not support property "${track.propertyPath}"`
        )
      }

      this.resolvedTracks.push({
        track,
        target,
        sample: {
          propertyPath: track.propertyPath,
          valueType: track.valueType,
          value: [],
          track,
        },
      })
    }
  }

  get state(): AnimationPlayerState {
    return this._state
  }

  get time(): number {
    return normalizeAnimationTime(this._time, this.duration, this.loopMode).time
  }

  get speed(): number {
    return this._speed
  }

  play(): void {
    if (this._state === 'playing') {
      return
    }
    if (this._state === 'completed') {
      this._time = this._speed >= 0 ? 0 : this.duration
    }
    this._state = 'playing'
    this.applyAtCurrentTime()
    this.emit('play')
  }

  pause(): void {
    if (this._state !== 'playing') {
      return
    }
    this._state = 'paused'
    this.emit('pause')
  }

  resume(): void {
    if (this._state !== 'paused') {
      return
    }
    this._state = 'playing'
    this.emit('resume')
  }

  stop(): void {
    if (this._state === 'stopped' || this._state === 'completed') {
      return
    }
    this._state = 'stopped'
    this.emit('stop')
  }

  sampleAt(time: number): void {
    this.assertFiniteTime(time, 'Animation sample time must be finite')
    const normalized = normalizeAnimationTime(time, this.duration, this.loopMode)
    this.applyAtTime(normalized.time)
  }

  seekAndApply(time: number): void {
    this.seek(time)
  }

  seek(time: number): void {
    this.assertFiniteTime(time, 'Animation seek time must be finite')
    this._time = time
    this.applyAtCurrentTime()
    this.emit('seek')
  }

  setSpeed(speed: number): void {
    if (!Number.isFinite(speed)) {
      throw new Error('Animation speed must be finite')
    }
    this._speed = speed
  }

  update(deltaSeconds: number): void {
    if (this._state !== 'playing') {
      return
    }
    if (!Number.isFinite(deltaSeconds)) {
      throw new Error('Animation deltaSeconds must be finite')
    }

    this._time += deltaSeconds * this._speed
    const normalized = normalizeAnimationTime(this._time, this.duration, this.loopMode)
    this.applyAtTime(normalized.time)

    if (normalized.completed) {
      this._time = normalized.time
      this._state = 'completed'
      this.emit('complete')
      return
    }

    this.emit('update')
  }

  hasTargetKey(key: string): boolean {
    return this.resolvedTracks.some((entry) => entry.target.key === key)
  }

  hasTargetRef(targetRef: AnimationTargetRef): boolean {
    return this.resolvedTracks.some((entry) => {
      const resolved = entry.target.targetRef
      if (resolved.kind !== targetRef.kind) {
        return false
      }
      if ('viewId' in targetRef && targetRef.viewId !== undefined) {
        return 'viewId' in resolved && resolved.viewId === targetRef.viewId
      }
      if ('id' in targetRef && targetRef.id !== undefined) {
        return 'id' in resolved && resolved.id === targetRef.id
      }
      if ('namespace' in targetRef && targetRef.namespace !== undefined) {
        return 'namespace' in resolved && resolved.namespace === targetRef.namespace
      }
      return true
    })
  }

  on(
    event: AnimationPlayerEvent,
    callback: (payload: AnimationPlayerEventPayload) => void
  ): Disposable {
    let callbacks = this.listeners.get(event)
    if (!callbacks) {
      callbacks = new Set()
      this.listeners.set(event, callbacks)
    }
    callbacks.add(callback)
    return {
      dispose: () => {
        callbacks?.delete(callback)
      },
    }
  }

  dispose(): void {
    this.stop()
    this.listeners.clear()
    this.sampleGroups.clear()
  }

  private applyAtCurrentTime(): void {
    const normalized = normalizeAnimationTime(this._time, this.duration, this.loopMode)
    this.applyAtTime(normalized.time)
  }

  private assertFiniteTime(time: number, message: string): void {
    if (!Number.isFinite(time)) {
      throw new Error(message)
    }
  }

  private applyAtTime(time: number): void {
    this.sampleGroups.clear()
    for (const entry of this.resolvedTracks) {
      sampleAnimationTrack(
        entry.track,
        time,
        {
          duration: this.duration,
          loopMode: this.loopMode,
        },
        entry.sample.value
      )

      let group = this.sampleGroups.get(entry.target.key)
      if (!group) {
        group = {
          target: entry.target,
          samples: [],
        }
        this.sampleGroups.set(entry.target.key, group)
      }
      group.samples.push(entry.sample)
    }

    const context: AnimationApplyContext = {
      player: this,
      time,
    }
    for (const group of this.sampleGroups.values()) {
      group.target.write(group.samples, context)
    }
  }

  private emit(event: AnimationPlayerEvent): void {
    const callbacks = this.listeners.get(event)
    if (!callbacks || callbacks.size === 0) {
      return
    }

    const payload: AnimationPlayerEventPayload = {
      player: this,
      time: this.time,
    }
    for (const callback of Array.from(callbacks)) {
      callback(payload)
    }
  }
}
